import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getEquipment, getLoans, getSales, getReportsSummary } from "@/lib/api";
import { 
  Monitor, 
  ArrowLeftRight, 
  ShoppingCart, 
  Download,
  FileSpreadsheet,
  TrendingUp,
  Package,
  Users
} from "lucide-react";

export default function Reports() {
  const [equipment, setEquipment] = useState([]);
  const [loans, setLoans] = useState([]);
  const [sales, setSales] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [equipmentRes, loansRes, salesRes, summaryRes] = await Promise.all([
        getEquipment(),
        getLoans(),
        getSales(),
        getReportsSummary()
      ]);
      setEquipment(equipmentRes.data);
      setLoans(loansRes.data);
      setSales(salesRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error("Error loading reports:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString("de-DE");
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (value, currency = "TND") => {
    if (currency === "TND") {
      return `${(value || 0).toFixed(3)} TND`;
    }
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: currency || "EUR"
    }).format(value || 0);
  };

  const exportToCSV = (data, filename, headers) => {
    const csvContent = [
      headers.join(";"),
      ...data.map(row => headers.map(h => row[h.toLowerCase().replace(/\s/g, "_")] || "").join(";"))
    ].join("\n");
    
    const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
  };

  const exportEquipment = () => {
    const data = equipment.map(e => ({
      seriennummer: e.serial_number,
      bezeichnung: e.name,
      kategorie: e.category_name,
      zustand: e.condition,
      status: e.status,
      kaufdatum: formatDate(e.purchase_date),
      kaufpreis: e.purchase_price
    }));
    exportToCSV(data, "inventar", ["Seriennummer", "Bezeichnung", "Kategorie", "Zustand", "Status", "Kaufdatum", "Kaufpreis"]);
  };

  const exportLoans = () => {
    const data = loans.map(l => ({
      equipment: l.equipment_name,
      seriennummer: l.equipment_serial,
      mitarbeiter: l.employee_name,
      ausleihdatum: formatDate(l.loan_date),
      rückgabe: formatDate(l.actual_return_date),
      status: l.status,
      zustand_bei_rückgabe: l.condition_at_return || "-"
    }));
    exportToCSV(data, "ausleihen", ["Equipment", "Seriennummer", "Mitarbeiter", "Ausleihdatum", "Rückgabe", "Status", "Zustand_bei_Rückgabe"]);
  };

  const exportSales = () => {
    const data = sales.map(s => ({
      equipment: s.equipment_name,
      seriennummer: s.equipment_serial,
      käufer: s.buyer_name,
      verkaufsdatum: formatDate(s.sale_date),
      preis: s.sale_price
    }));
    exportToCSV(data, "verkäufe", ["Equipment", "Seriennummer", "Käufer", "Verkaufsdatum", "Preis"]);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="reports-loading">
        <div className="text-slate-500">Lädt...</div>
      </div>
    );
  }

  return (
    <div data-testid="reports-page">
      <div className="page-header">
        <h1 className="page-title">Reports</h1>
        <p className="page-subtitle">Übersichten und Berichte exportieren</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card className="metric-card">
          <div className="flex items-start justify-between">
            <div>
              <p className="metric-card-label">Gesamtinventar</p>
              <p className="metric-card-value">{summary?.total_equipment || 0}</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Package className="w-6 h-6 text-primary" strokeWidth={1.5} />
            </div>
          </div>
        </Card>

        <Card className="metric-card">
          <div className="flex items-start justify-between">
            <div>
              <p className="metric-card-label">Inventarwert</p>
              <p className="metric-card-value text-xl">{formatCurrency(summary?.total_value)}</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-violet-50 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-violet-600" strokeWidth={1.5} />
            </div>
          </div>
        </Card>

        <Card className="metric-card">
          <div className="flex items-start justify-between">
            <div>
              <p className="metric-card-label">Aktive Ausleihen</p>
              <p className="metric-card-value text-amber-600">{summary?.active_loans || 0}</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center">
              <ArrowLeftRight className="w-6 h-6 text-amber-600" strokeWidth={1.5} />
            </div>
          </div>
        </Card>

        <Card className="metric-card">
          <div className="flex items-start justify-between">
            <div>
              <p className="metric-card-label">Verkaufserlös</p>
              <p className="metric-card-value text-xl text-green-600">{formatCurrency(summary?.sales_revenue)}</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center">
              <ShoppingCart className="w-6 h-6 text-green-600" strokeWidth={1.5} />
            </div>
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="inventory" className="space-y-6">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="inventory" className="gap-2" data-testid="tab-inventory">
            <Monitor className="w-4 h-4" />
            Inventar
          </TabsTrigger>
          <TabsTrigger value="loans" className="gap-2" data-testid="tab-loans">
            <ArrowLeftRight className="w-4 h-4" />
            Ausleihhistorie
          </TabsTrigger>
          <TabsTrigger value="sales" className="gap-2" data-testid="tab-sales">
            <ShoppingCart className="w-4 h-4" />
            Verkäufe
          </TabsTrigger>
        </TabsList>

        {/* Inventory Tab */}
        <TabsContent value="inventory">
          <Card className="border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between pb-4">
              <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Inventarübersicht
              </CardTitle>
              <Button variant="outline" onClick={exportEquipment} className="gap-2" data-testid="export-inventory-btn">
                <FileSpreadsheet className="w-4 h-4" />
                CSV Export
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/50">
                    <TableHead>Seriennummer</TableHead>
                    <TableHead>Bezeichnung</TableHead>
                    <TableHead>Kategorie</TableHead>
                    <TableHead>Zustand</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Kaufpreis</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {equipment.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                        Keine Einträge vorhanden
                      </TableCell>
                    </TableRow>
                  ) : (
                    equipment.map((item) => (
                      <TableRow key={item.id} className="table-row-hover">
                        <TableCell className="font-mono text-sm">{item.serial_number}</TableCell>
                        <TableCell className="font-medium">{item.name}</TableCell>
                        <TableCell>{item.category_name}</TableCell>
                        <TableCell>{item.condition}</TableCell>
                        <TableCell>
                          <span className={
                            item.status === "Verfügbar" ? "badge-success" :
                            item.status === "Ausgeliehen" ? "badge-warning" : "badge-neutral"
                          }>
                            {item.status}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(item.purchase_price, item.currency)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Loans Tab */}
        <TabsContent value="loans">
          <Card className="border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between pb-4">
              <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Ausleihhistorie
              </CardTitle>
              <Button variant="outline" onClick={exportLoans} className="gap-2" data-testid="export-loans-btn">
                <FileSpreadsheet className="w-4 h-4" />
                CSV Export
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/50">
                    <TableHead>Equipment</TableHead>
                    <TableHead>Mitarbeiter</TableHead>
                    <TableHead>Ausleihdatum</TableHead>
                    <TableHead>Rückgabe</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Zustand b. Rückgabe</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loans.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                        Keine Ausleihen vorhanden
                      </TableCell>
                    </TableRow>
                  ) : (
                    loans.map((loan) => (
                      <TableRow key={loan.id} className="table-row-hover">
                        <TableCell>
                          <div>
                            <p className="font-medium">{loan.equipment_name}</p>
                            <p className="text-sm text-slate-500">{loan.equipment_serial}</p>
                          </div>
                        </TableCell>
                        <TableCell>{loan.employee_name}</TableCell>
                        <TableCell>{formatDate(loan.loan_date)}</TableCell>
                        <TableCell>{formatDate(loan.actual_return_date)}</TableCell>
                        <TableCell>
                          <span className={loan.status === "Ausgeliehen" ? "badge-warning" : "badge-success"}>
                            {loan.status}
                          </span>
                        </TableCell>
                        <TableCell>{loan.condition_at_return || "-"}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sales Tab */}
        <TabsContent value="sales">
          <Card className="border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between pb-4">
              <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Verkaufsübersicht
              </CardTitle>
              <Button variant="outline" onClick={exportSales} className="gap-2" data-testid="export-sales-btn">
                <FileSpreadsheet className="w-4 h-4" />
                CSV Export
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/50">
                    <TableHead>Equipment</TableHead>
                    <TableHead>Seriennummer</TableHead>
                    <TableHead>Käufer</TableHead>
                    <TableHead>Verkaufsdatum</TableHead>
                    <TableHead className="text-right">Verkaufspreis</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sales.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                        Keine Verkäufe vorhanden
                      </TableCell>
                    </TableRow>
                  ) : (
                    sales.map((sale) => (
                      <TableRow key={sale.id} className="table-row-hover">
                        <TableCell className="font-medium">{sale.equipment_name}</TableCell>
                        <TableCell className="font-mono text-sm">{sale.equipment_serial}</TableCell>
                        <TableCell>{sale.buyer_name}</TableCell>
                        <TableCell>{formatDate(sale.sale_date)}</TableCell>
                        <TableCell className="text-right font-semibold text-green-600">
                          {formatCurrency(sale.sale_price)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
